import {
  Box,
  FormControl,
  FormLabel,
  Input
} from "@chakra-ui/react";
import React, {
  useEffect,
  useRef,
  useState
} from "react";
import {
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form";
import { Select } from "chakra-react-select";
import { addMinutes } from "date-fns";

import { useMissionsApi } from "src/api/missions/missions.api";
import { SolidButton } from "src/components/button";
import {
  DatePicker,
  TimePicker
} from "src/components/pickers";
import { Text } from "src/components/text";
import { pickerDateToUtcKey } from "src/utils/utcDate";

import { AddParticipants } from "./AddParticipants";
import { ConversationMode } from "../../../../api";

export interface FormProps {
  date: Date
  startTime: Date
  endTime: Date
  latencyMinutes: number
  latencySeconds: number
}

interface ConversationFormProps {
  handleSubmit: UseFormHandleSubmit<FormProps>;
  register: UseFormRegister<FormProps>
  setSelectedParter: (partnerId: string) => void;
  onSubmit: (data: FormProps) => void;
  selectedPartner?: string;
  isNew: boolean;
  setMode: (option: SelectModeOption) => void
  mode: SelectModeOption;
  watch: UseFormWatch<FormProps>
  setValue: UseFormSetValue<FormProps>
}

export interface SelectModeOption {
  label: string,
  value: ConversationMode
}

const modeOptions = [
  {
    label: "Message",
    value: ConversationMode.MESSAGE
  },
  {
    label: "Voice",
    value: ConversationMode.VOICE
  }
];

export const CreateEditConversationForm: React.FC<ConversationFormProps> = ({
  handleSubmit, onSubmit, setSelectedParter, selectedPartner, isNew, setMode, mode, setValue, watch
}) => {
  const date = watch("date");
  const latencyMinutes = watch("latencyMinutes");
  const latencySeconds = watch("latencySeconds");
  const dayKey = date ? pickerDateToUtcKey(date) : "";
  const [ latencyRes, latencyReq ] = useMissionsApi("GET_LATENCY");
  const [ latencyEditable, setLatencyEditable ] = useState(false);
  const requestedKey = useRef("");
  const snapshot = useRef<{ key: string; minutes: number; seconds: number } | null>(null);

  const currentLatency = useRef({
    minutes: 0,
    seconds: 0
  });

  currentLatency.current = {
    minutes: Number(latencyMinutes) || 0,
    seconds: Number(latencySeconds) || 0
  };

  useEffect(() => {
    if (!dayKey) {
      return;
    }

    if (!isNew && snapshot.current === null) {
      snapshot.current = {
        key: dayKey,
        minutes: currentLatency.current.minutes,
        seconds: currentLatency.current.seconds
      };
    }

    requestedKey.current = dayKey;
    latencyReq({ date: dayKey });
  }, [
    dayKey,
    isNew,
    latencyReq
  ]);

  useEffect(() => {
    if (!latencyRes.data || !dayKey || requestedKey.current !== dayKey) {
      return;
    }

    if (latencyRes.data.date && latencyRes.data.date !== dayKey) {
      return;
    }

    setLatencyEditable(latencyRes.data.editable === true);

    if (snapshot.current?.key === dayKey) {
      setValue("latencyMinutes", snapshot.current.minutes, { shouldDirty: true });
      setValue("latencySeconds", snapshot.current.seconds, { shouldDirty: true });

      return;
    }

    setValue("latencyMinutes", Math.floor(latencyRes.data.latencySeconds / 60), { shouldDirty: true });
    setValue("latencySeconds", latencyRes.data.latencySeconds % 60, { shouldDirty: true });
  }, [
    latencyRes.data,
    dayKey,
    setValue
  ]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        display="flex"
        flexDirection="row"
      >

        <Box
          display="flex"
          flexDirection="column"
          width="50%"
        >
          <FormControl
            mt={10}

            id="mode"
          >
            <FormLabel>
              Select mode
            </FormLabel>

            <Select
              value={{
                label: mode.label,
                value: mode.value
              }}
              onChange={(e: SelectModeOption) => setMode(e)}
              placeholder="Select Mode"
              options={modeOptions}
              closeMenuOnSelect
              size="md"
            />

          </FormControl>

          <FormControl
            paddingY={5}
            id="date"
          >
            <FormLabel>
              Date
            </FormLabel>

            <DatePicker
              name="date"
              onChange={value => {
                if (!value) {
                  return;
                }

                setValue("date", new Date(value.getFullYear(), value.getMonth(), value.getDate()), {
                  shouldDirty: true,
                  shouldTouch: true
                });
              }}
              value={watch("date")}
              picker="date"
              format="DD-MM-YYYY"
              clearIcon={false}
            />

          </FormControl>

          <Box
            display="flex"
            flexDirection="row"
            paddingY={5}
          >
            <FormControl
              paddingRight={5}
              id="startTime"
            >
              <FormLabel>
                Start time
              </FormLabel>

              <TimePicker
                name="startTime"
                onChange={time => time && setValue("startTime", time)}
                value={watch("startTime")}
                clearIcon={false}
                format="HH:mm"
                showNow={false}
                onSelect={time => time && setValue("startTime", time)}
              />
            </FormControl>

            <FormControl id="endTime">
              <FormLabel>
                End time
              </FormLabel>

              <TimePicker
                name="endTime"
                onChange={time => time && setValue("endTime", time)}
                clearIcon={false}
                showNow={false}
                value={watch("endTime")}
                format="HH:mm"
                onSelect={time => time && setValue("endTime", time)}

              />
            </FormControl>
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            paddingY={5}
          >
            <FormControl
              paddingRight={5}
              id="latencyMinutes"
            >
              <FormLabel>
                Latency (mins)
              </FormLabel>

              <Input
                type="number"
                min={0}
                value={Number.isFinite(latencyMinutes) ? latencyMinutes : 0}
                onChange={event => setValue("latencyMinutes", event.target.value === "" ? 0 : Number(event.target.value), {
                  shouldDirty: true,
                  shouldValidate: true
                })}
                isReadOnly={!latencyEditable}
              />
            </FormControl>

            <FormControl id="latencySeconds">
              <FormLabel>
                Latency (secs)
              </FormLabel>

              <Input
                type="number"
                min={0}
                max={59}
                value={Number.isFinite(latencySeconds) ? latencySeconds : 0}
                onChange={event => setValue("latencySeconds", event.target.value === "" ? 0 : Number(event.target.value), {
                  shouldDirty: true,
                  shouldValidate: true
                })}
                isReadOnly={!latencyEditable}
              />
            </FormControl>
          </Box>

          <SolidButton
            type="submit"
          >
            {`${!isNew ? "Update" : "Create"} session`}
          </SolidButton>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          width="50%"
        >
          {isNew && (

            <AddParticipants
              selectedPartner={selectedPartner}
              onSelect={participantId => setSelectedParter(participantId)}
            />
          )}

        </Box>
      </Box>
    </form>
  );
};